import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Pencil } from "lucide-react";

export default function AdminQuestions() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [testNumber, setTestNumber] = useState(1);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    text: "", optA: "", optB: "", optC: "", optD: "",
    correct_index: 0, explanation: "", topic: "General", difficulty: "medium",
  });

  const emptyForm = () => ({
    text: "", optA: "", optB: "", optC: "", optD: "",
    correct_index: 0, explanation: "", topic: "General", difficulty: "medium",
  });

  const startEdit = (q) => {
    setEditingId(q.id);
    setForm({
      text: q.text,
      optA: q.options[0] || "",
      optB: q.options[1] || "",
      optC: q.options[2] || "",
      optD: q.options[3] || "",
      correct_index: q.correct_index,
      explanation: q.explanation || "",
      topic: q.topic || "General",
      difficulty: q.difficulty || "medium",
    });
    setOpen(true);
  };

  const startCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  useEffect(() => {
    api.get("/courses?limit=100").then((r) => {
      setCourses(r.data);
      if (r.data.length && !courseId) setCourseId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = useCallback(() => {
    if (!courseId) return;
    api.get(`/admin/questions?course_id=${courseId}&test_number=${testNumber}`).then((r) => setItems(r.data));
  }, [courseId, testNumber]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        course_id: courseId,
        test_number: parseInt(testNumber),
        text: form.text,
        options: [form.optA, form.optB, form.optC, form.optD],
        correct_index: parseInt(form.correct_index),
        explanation: form.explanation,
        topic: form.topic,
        difficulty: form.difficulty,
      };
      if (editingId) {
        await api.put(`/admin/questions/${editingId}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/admin/questions", payload);
        toast.success("Question added");
      }
      setOpen(false);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/questions/${id}`);
    load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/questions/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.error_count) {
        toast.warning(`Imported ${data.inserted}; ${data.error_count} row(s) need fixes`);
      } else {
        toast.success(`Imported ${data.inserted} questions`);
      }
      load();
    } catch (e) {
      toast.error("CSV upload failed");
    }
    e.target.value = "";
  };

  return (
    <div data-testid="admin-questions-page">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Questions</h1>
          <p className="text-muted-foreground text-sm">Manage questions per course & test.</p>
        </div>
        <div className="flex gap-2">
          <label className="btn-ghost cursor-pointer" data-testid="csv-upload">
            <Upload className="h-4 w-4" /> Bulk CSV
            <input type="file" accept=".csv" onChange={upload} className="hidden" />
          </label>
          <button onClick={startCreate} className="btn-primary" data-testid="add-question-button"><Plus className="h-4 w-4" /> New question</button>
        </div>
      </div>

      <div className="flex gap-3 mb-6">
        <select value={courseId} onChange={(e) => setCourseId(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm" data-testid="filter-course">
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <select value={testNumber} onChange={(e) => setTestNumber(parseInt(e.target.value))} className="h-10 rounded-md border border-border bg-background px-3 text-sm" data-testid="filter-test-number">
          {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>Mock test #{n}</option>)}
        </select>
        <div className="text-sm text-muted-foreground self-center">{items.length} questions</div>
      </div>

      <div className="space-y-3">
        {items.map((q, i) => (
          <div key={q.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold">Q{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{q.text}</div>
                <div className="mt-1 text-xs text-muted-foreground">{q.topic} · {q.difficulty} · correct: {String.fromCharCode(65 + q.correct_index)}</div>
              </div>
              <button onClick={() => startEdit(q)} className="text-foreground/70 hover:bg-accent rounded p-1.5" data-testid={`edit-question-${q.id}`}><Pencil className="h-4 w-4" /></button>
              <button onClick={() => del(q.id)} className="text-rose-500 hover:bg-rose-500/10 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No questions yet for this test.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-6">
          <div className="bg-background rounded-xl border border-border max-w-2xl w-full p-6 my-8">
            <h2 className="font-display text-2xl mb-4">{editingId ? "Edit question" : "New question"}</h2>
            <div className="space-y-3">
              <textarea rows={3} placeholder="Question text" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
              {["A", "B", "C", "D"].map((l, i) => (
                <input key={l} placeholder={`Option ${l}`} value={form[`opt${l}`]} onChange={(e) => setForm({ ...form, [`opt${l}`]: e.target.value })} className="h-10 w-full rounded-md border border-border bg-background px-3 text-sm" />
              ))}
              <div className="grid sm:grid-cols-3 gap-3">
                <select value={form.correct_index} onChange={(e) => setForm({ ...form, correct_index: e.target.value })} className="h-10 rounded-md border border-border bg-background px-3 text-sm">
                  <option value={0}>Correct: A</option><option value={1}>Correct: B</option><option value={2}>Correct: C</option><option value={3}>Correct: D</option>
                </select>
                <input placeholder="Topic" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} className="h-10 rounded-md border border-border bg-background px-3 text-sm" />
                <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="h-10 rounded-md border border-border bg-background px-3 text-sm">
                  <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
                </select>
              </div>
              <textarea rows={3} placeholder="Explanation" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm" />
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button onClick={() => { setOpen(false); setEditingId(null); }} className="btn-ghost">Cancel</button>
              <button onClick={save} className="btn-primary">{editingId ? "Update question" : "Save question"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
